import { type SxProps, type Theme } from "@mui/material/styles";

import { borderRadius, shadows } from "design-tokens";
import { mergeSx } from "ui";

/** `h-10`, and `min-h-10` in the compact size. */
const ITEM_HEIGHT = 40;

const BASE_SX: SxProps<Theme> = (theme) => ({
  position: "relative",
  display: "flex",
  width: "100%",
  borderRadius: borderRadius.md,
  color: theme.palette.grey[900],
  cursor: "pointer",
  outlineStyle: "none",
});

const EXPANDED_SX: SxProps<Theme> = {
  height: ITEM_HEIGHT,
  alignItems: "center",
  overflow: "hidden",
  pl: 1.5,
  pr: 1,
};

const EXPANDED_COMPACT_SX: SxProps<Theme> = {
  minHeight: ITEM_HEIGHT,
  alignItems: "center",
  justifyContent: "flex-start",
  textAlign: "left",
  pl: 1.5,
  pr: 1,
  py: 1,
};

const COLLAPSED_SX: SxProps<Theme> = {
  height: ITEM_HEIGHT,
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  px: 1.5,
};

const HOVER_SX: SxProps<Theme> = (theme) => ({
  "&:hover": { backgroundColor: theme.palette.grey[100] },
});

const ACTIVE_SX: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.palette.grey[200],
  fontWeight: 600,
});

/**
 * A collapsed row that opens a flyout reserves the 1px its open state paints and pulls it back out
 * of the layout again, so opening the menu does not nudge the row or its neighbours.
 */
const FLYOUT_FRAME_SX: SxProps<Theme> = {
  border: "1px solid transparent",
  mx: "-1px",
};

/**
 * The open flyout drops the row's own active fill — the flyout itself marks the selected child — but
 * keeps hover, so the trigger still responds while the menu is up.
 */
const FLYOUT_OPEN_SX: SxProps<Theme> = (theme) => ({
  borderColor: theme.palette.primary.main,
  boxShadow: shadows.input,
  backgroundColor: "inherit",
  "&:hover": { backgroundColor: theme.palette.grey[100] },
});

/**
 * Figma's `sidebar/section/group/nav-items` focused state (node `19128:5162`): a 1px `primary.main`
 * edge with a 2px `aqua-100` ring outside it. The edge is an inset `outline` rather than a border so
 * a focused row does not reflow its content by a pixel, and the ring is `shadows.input`, the token
 * `MuiOutlinedInput` already focuses with — `aqua-100` has no palette slot of its own, which is what
 * justifies reading the token directly here.
 *
 * Longhands because jsdom's CSSOM does not report the `outline` shorthand back, so the shorthand form
 * leaves the focus-ring test asserting against an empty string.
 */
const FOCUS_SX: SxProps<Theme> = (theme) => ({
  "&:focus-visible": {
    outlineWidth: "1px",
    outlineStyle: "solid",
    outlineColor: theme.palette.primary.main,
    outlineOffset: "-1px",
    boxShadow: shadows.input,
  },
});

export type SidebarMenuItemSxOptions = {
  isCollapsed: boolean;
  isCompact: boolean;
  isActive?: boolean;
  /** The expanded group header carries no fill of its own — its children show the active row. */
  hasFillStates?: boolean;
  hasFlyout?: boolean;
  isFlyoutOpen?: boolean;
};

const getLayoutSx = (isCollapsed: boolean, isCompact: boolean) => {
  if (isCollapsed) {
    return COLLAPSED_SX;
  }

  return isCompact ? EXPANDED_COMPACT_SX : EXPANDED_SX;
};

export const getSidebarMenuItemSx = ({
  isCollapsed,
  isCompact,
  isActive = false,
  hasFillStates = true,
  hasFlyout = false,
  isFlyoutOpen = false,
}: SidebarMenuItemSxOptions): SxProps<Theme> => {
  const hasFlyoutFrame = hasFlyout && isCollapsed;

  return mergeSx(
    BASE_SX,
    getLayoutSx(isCollapsed, isCompact),
    hasFlyoutFrame ? FLYOUT_FRAME_SX : undefined,
    hasFillStates ? (isActive ? ACTIVE_SX : HOVER_SX) : undefined,
    hasFlyoutFrame && isFlyoutOpen ? FLYOUT_OPEN_SX : undefined,
    FOCUS_SX,
  );
};
